// eslint-disable-next-line no-use-before-define
import React, { useEffect, useState, useContext } from 'react';
import { useQuery } from '@apollo/client';
import { useRouteMatch, useLocation } from 'react-router-dom';
import { useErrorModalContext } from 'op-contexts';

import { ROSidePanel, RODoseSiteCarousel } from 'op-components';
import { groupBy } from 'op-utils';
import { GeneralError, LoadingSpinner } from 'shared-components/components';

import { CurrentAppConfig } from '../AppConfig';
import { GET_DIAGNOSIS_FROM_CAREPLAN } from './SidePanelQueries';
import ROPatientDiagnosisCurrentCode from '../DiagnosisPages/CurrentCode';
import { GET_DOSE_SITE_SUMMARIES } from '../../PatientSummary/PatientProfileQueries';
import './SidePanel.scss';
import ROPeerReviewSidePanel from './PeerReviewSidePanel';
import { Stack, useTheme, CustomTheme } from '@mui/material';
import SidePanelItem from './SidePanelItem';
import { TenantContext } from 'op-contexts';
import { doseRounding } from 'op-utils';
import { TenantContextType } from 'op-contexts/TenantContext/TenantContext';
import ROPatientPrescriptionsWidget from './PrescriptionsSidePanel';
export const getOTVCareplanName = (doseSiteSummary: any, tenantContext: TenantContextType): string => {
  const { doseUnit, fractionUnit } = tenantContext;
  const doseValue = doseRounding(doseSiteSummary.plannedDosageQuantity, doseSiteSummary.isAria, tenantContext);
  const doseFractions = doseSiteSummary.plannedDosageRatio;
  const technique = doseSiteSummary.technique !== null ? doseSiteSummary.technique : '';
  return `${doseSiteSummary.identifierSite} ${technique} ${doseValue}${doseUnit}/${doseFractions}${fractionUnit}`;
};

const ROPatientCarePathDiagnosisWidget = (): JSX.Element => {
  const match: { params: { careplanId: string } } = useRouteMatch();
  const location = useLocation();
  const { careplanId } = match.params;
  const { loading, data } = useQuery(GET_DIAGNOSIS_FROM_CAREPLAN, { variables: { careplanId: careplanId } });

  // Check if we're on the treatment information page
  const isOnTreatmentInfoPage = location.pathname.includes('treatmentInfo');

  if (loading) return <div>Loading</div>;
  if (!data) return <div />;
  const { diagnosis } = data.careplan;
  const primaryDiagnosisCode = diagnosis.isPrimaryDiagnosis ? diagnosis : diagnosis.relatedPrimaryDiagnosis;
  const secondaryDiagnosisCode = diagnosis.isPrimaryDiagnosis ? null : diagnosis;
  return (
    <>
      {primaryDiagnosisCode && (
        <ROPatientDiagnosisCurrentCode
          showTitle={true}
          showExpandButton={true}
          diagnosisCodeSet={primaryDiagnosisCode}
          showPrimary={true}
          initialExpanded={isOnTreatmentInfoPage}
        />
      )}
      {secondaryDiagnosisCode && (
        <ROPatientDiagnosisCurrentCode
          showTitle={true}
          diagnosisCodeSet={secondaryDiagnosisCode}
          showPrimary={false}
          initialExpanded={isOnTreatmentInfoPage}
        />
      )}
    </>
  );
};

interface DoseSummaryWidgetProps {
  selectedCareplan?: string;
}

export const ROPatientCarePathDoseSummaryWidget = ({ selectedCareplan }: DoseSummaryWidgetProps): JSX.Element => {
  const match: { params: { id: string } } = useRouteMatch();
  const tenantContext = useContext(TenantContext);
  const { setError } = useErrorModalContext();

  const { id: patientId } = match.params;
  const [index, setIndex] = useState(0);
  const { data, loading, error } = useQuery(GET_DOSE_SITE_SUMMARIES, {
    variables: { patientId },
    fetchPolicy: 'cache-and-network',
  });
  const theme = useTheme<CustomTheme>();

  useEffect(() => {
    if (error) {
      return setError(null);
    }
  }, [error]);

  useEffect(() => {
    if (!data || data?.doseSiteSummaries.length === 0) {
      return;
    }
    if (selectedCareplan) {
      const selectedCareplanDoseSite = data.doseSiteSummaries.find(
        (dss: any) => getOTVCareplanName(dss, tenantContext) === selectedCareplan,
      );
      if (selectedCareplanDoseSite) {
        const index = data.doseSiteSummaries.indexOf(selectedCareplanDoseSite);
        setIndex(index);
        return;
      }
    }
    setIndex(0);
  }, [data]);

  if (!data && loading)
    return (
      <Stack sx={{ width: '100%' }} className="px-0">
        <LoadingSpinner loadingText={'Loading dose site summaries'} relativeSpinner={true} />
      </Stack>
    );

  let container = (
    <GeneralError
      primaryText="Unable to load dose site summaries."
      secondaryText={`Please contact ${theme.custom.support}.`}
    />
  );

  let tracker;

  if (data) {
    const mqDoses = data.doseSiteSummaries.filter((dss: any) => !dss.isAria);
    const ariaDoses = data.doseSiteSummaries.filter((dss: any) => dss.isAria);
    const groupedDose = groupBy(ariaDoses, 'categorySiteGroup');
    const doseSize = mqDoses.length + Object.keys(groupedDose).length;
    const handleSelect = (selectedIndex: number) => {
      setIndex(selectedIndex % doseSize);
    };
    tracker = doseSize ? `${index + 1}/${doseSize}` : '';
    container = (
      <RODoseSiteCarousel
        handleSelect={handleSelect}
        index={index}
        mqDoses={mqDoses}
        ariaDoses={ariaDoses}
        doseSize={doseSize}
      />
    );
  }

  return (
    <SidePanelItem title={`Dose site summary ${tracker}`} topBorderColor={theme.palette.primary.main}>
      <Stack sx={{ padding: '4px 10px' }}>{container}</Stack>
    </SidePanelItem>
  );
};

interface ROPatientCarePathSidePanelProps {
  onPrescriptionDeleted?: (prescriptionName: string) => void;
  onPrescriptionDeleteFailed?: (prescriptionName: string) => void;
}

const ROPatientCarePathSidePanel = ({
  onPrescriptionDeleted,
  onPrescriptionDeleteFailed,
}: ROPatientCarePathSidePanelProps = {}): JSX.Element => {
  return (
    <div className="carepath-site-panel-component">
      <ROSidePanel>
        <ROPatientCarePathDiagnosisWidget />
        <ROPatientPrescriptionsWidget
          onPrescriptionDeleted={onPrescriptionDeleted}
          onPrescriptionDeleteFailed={onPrescriptionDeleteFailed}
        />
        <ROPeerReviewSidePanel />
        {CurrentAppConfig.DoseSiteSummary.enabled && <ROPatientCarePathDoseSummaryWidget />}
      </ROSidePanel>
    </div>
  );
};

export default ROPatientCarePathSidePanel;
